import { useCallback } from 'react';
import analyticsTrackingService, { AnalyticsEventType } from '../services/analyticsTrackingService';

/**
 * Hook for tracking analytics events
 *
 * Usage:
 * ```tsx
 * const { trackSignupClick, trackGoogleSignup, trackMicrosoftSignup } = useAnalyticsTracking();
 *
 * // In your component
 * <button onClick={() => { trackSignupClick(); navigateToSignup(); }}>
 *   Sign Up
 * </button>
 * ```
 */
export const useAnalyticsTracking = () => {
  /**
   * Track a generic event
   */
  const trackEvent = useCallback(
    (eventType: AnalyticsEventType, metadata?: Record<string, unknown>) => {
      analyticsTrackingService.trackEvent({ eventType, metadata });
    },
    []
  );

  /**
   * Track email sign-up button click
   */
  const trackSignupClick = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackSignupClick(metadata);
  }, []);

  /**
   * Track Google OAuth sign-up button click
   */
  const trackGoogleSignup = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackGoogleSignupClick(metadata);
  }, []);

  /**
   * Track Microsoft OAuth sign-up button click
   */
  const trackMicrosoftSignup = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackMicrosoftSignupClick(metadata);
  }, []);

  /**
   * Track email/password sign-up form submission
   */
  const trackEmailSignup = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackEmailSignupClick(metadata);
  }, []);

  /**
   * Track account creation
   */
  const trackAccountCreated = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackAccountCreated(metadata);
  }, []);

  /**
   * Track first meeting type creation
   */
  const trackFirstMeetingCreated = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackFirstMeetingCreated(metadata);
  }, []);

  /**
   * Track first booking received
   */
  const trackFirstBookingReceived = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackFirstBookingReceived(metadata);
  }, []);

  /**
   * Track subscription started
   */
  const trackSubscriptionStarted = useCallback((metadata?: Record<string, unknown>) => {
    analyticsTrackingService.trackSubscriptionStarted(metadata);
  }, []);

  return {
    trackEvent,
    trackSignupClick,
    trackGoogleSignup,
    trackMicrosoftSignup,
    trackEmailSignup,
    trackAccountCreated,
    trackFirstMeetingCreated,
    trackFirstBookingReceived,
    trackSubscriptionStarted,
  };
};

export default useAnalyticsTracking;
